/**
 * Flattening check-in sessions into one row per device, for the Check-Ins page.
 *
 * The list endpoint is session-shaped (that's how the records are stored), but
 * the question asked on the shop floor is about a *machine* — "where's the
 * Dell", "which job is serial ABC123 on". So a session with three devices
 * reads as three rows.
 *
 * Search is deliberately client-side: the serial lives inside the draft job's
 * inspection JSON, which Dataverse can't filter on, so there is no honest
 * server-side serial search to call. Callers must say what the search covers.
 */
import type {
  CheckInIntakeType,
  CheckInLifecycleStatus,
  CheckInSessionListDevice,
  CheckInSessionListItem,
} from '@techgitter/contracts';

export interface CheckInDeviceRow {
  /** `${sessionId}:${device.id}` — devices are unique within a session. */
  key: string;
  sessionId: string;
  sessionNumber: string;
  customerLabel: string;
  intakeType: CheckInIntakeType;
  lifecycleStatus: CheckInLifecycleStatus;
  /** Completion time where the session has one, else when it was raised. */
  occurredAt: string;
  completedAt: string | null;
  device: CheckInSessionListDevice;
}

/** Business name where there is one — that's how staff refer to the job. */
export function customerLabel(session: CheckInSessionListItem): string {
  return session.businessName || session.customerName || 'Unknown customer';
}

export function toDeviceRows(sessions: CheckInSessionListItem[]): CheckInDeviceRow[] {
  const rows: CheckInDeviceRow[] = [];
  for (const session of sessions) {
    for (const device of session.devices ?? []) {
      rows.push({
        key: `${session.id}:${device.id}`,
        sessionId: session.id,
        sessionNumber: session.sessionNumber,
        customerLabel: customerLabel(session),
        intakeType: session.intakeType,
        lifecycleStatus: session.lifecycleStatus,
        occurredAt: session.completedAt || session.createdAt,
        completedAt: session.completedAt,
        device,
      });
    }
  }
  return rows;
}

/** Newest first. */
export function sortDeviceRows(rows: CheckInDeviceRow[]): CheckInDeviceRow[] {
  return [...rows].sort((a, b) =>
    a.occurredAt > b.occurredAt ? -1 : a.occurredAt < b.occurredAt ? 1 : 0,
  );
}

/**
 * Case-insensitive match across the fields someone would actually type: the
 * customer, the session number, the RS ticket, and the device's make/serial.
 * An empty query matches everything.
 */
export function filterDeviceRows(rows: CheckInDeviceRow[], query: string): CheckInDeviceRow[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter(row => {
    const haystack = [
      row.customerLabel,
      row.sessionNumber,
      row.device.serviceSummary,
      row.device.deviceType.replace(/_/g, ' '),
      row.device.manufacturer,
      row.device.serialNumber,
      row.device.repairShoprTicketNumber,
      row.device.repairShoprTicketId,
    ];
    return haystack.some(value => !!value && value.toLowerCase().includes(needle));
  });
}
